import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, X, Navigation, Search } from 'lucide-react';
import { reverseGeocode, geocodeAddressCached } from '@/utils/osmGeocoding';
import { addBasemap, MAP_TILE_URL, MAP_TILE_OPTIONS } from '@/config/mapTiles';

interface DeliveryPinPickerProps {
  lat: number | null;
  lng: number | null;
  address: string;
  onChange: (data: { lat: number; lng: number; address: string }) => void;
}

const DEFAULT_LOCATION = { lat: 13.4549, lng: -16.5790 }; // Banjul
const TILE_URL = MAP_TILE_URL;
const TILE_OPTIONS = MAP_TILE_OPTIONS;


const DeliveryPinPicker = ({ lat, lng, address, onChange }: DeliveryPinPickerProps) => {
  const previewRef = useRef<HTMLDivElement>(null);
  const previewMap = useRef<L.Map | null>(null);
  const previewMarker = useRef<L.Marker | null>(null);

  const fullRef = useRef<HTMLDivElement>(null);
  const fullMap = useRef<L.Map | null>(null);
  const userDotRef = useRef<L.Marker | null>(null);
  const userAccuracyRef = useRef<L.Circle | null>(null);

  const [open, setOpen] = useState(false);
  const [tempCoords, setTempCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [tempAddress, setTempAddress] = useState(address);
  const [searchInput, setSearchInput] = useState('');
  const [userLoc, setUserLoc] = useState<{ lat: number; lng: number; accuracy?: number } | null>(null);
  const [locating, setLocating] = useState(false);

  const pinIcon = L.divIcon({
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div style="width:22px;height:22px;background:hsl(var(--primary));border-radius:50%;border:4px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.35);"></div>
      <div style="width:3px;height:18px;background:hsl(var(--primary));margin-top:-2px;border-radius:0 0 3px 3px;"></div>
    </div>`,
    className: '',
    iconSize: [24, 40],
    iconAnchor: [12, 40],
  });

  // Initialize preview map
  useEffect(() => {
    if (!previewRef.current || previewMap.current) return;
    if (lat == null || lng == null) return;

    const map = new L.Map(previewRef.current, {
      center: [lat, lng],
      zoom: 16,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
    });
    addBasemap(map);
    previewMarker.current = L.marker([lat, lng], { icon: pinIcon }).addTo(map);
    previewMap.current = map;
    const timers = [50, 200, 500].map((ms) => setTimeout(() => map.invalidateSize(), ms));
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => map.invalidateSize()) : null;
    ro?.observe(previewRef.current);

    return () => {
      timers.forEach(clearTimeout);
      ro?.disconnect();
      map.remove();
      previewMap.current = null;
      previewMarker.current = null;
    };
  }, [lat != null && lng != null]);

  // Update preview marker on coord change
  useEffect(() => {
    if (!previewMap.current || !previewMarker.current || lat == null || lng == null) return;
    previewMarker.current.setLatLng([lat, lng]);
    previewMap.current.setView([lat, lng], previewMap.current.getZoom());
  }, [lat, lng]);

  // Initialize full-screen map when opened
  useEffect(() => {
    if (!open || !fullRef.current || fullMap.current) return;

    const start = tempCoords || (lat != null && lng != null ? { lat, lng } : DEFAULT_LOCATION);

    const map = new L.Map(fullRef.current, {
      center: [start.lat, start.lng],
      zoom: 17,
      zoomControl: false,
      attributionControl: false,
    });
    addBasemap(map);
    fullMap.current = map;
    setTempCoords(start);

    const updateFromCenter = async () => {
      const c = map.getCenter();
      setTempCoords({ lat: c.lat, lng: c.lng });
    };
    map.on('moveend', updateFromCenter);
    const timers = [50, 200, 500, 1000].map((ms) => setTimeout(() => map.invalidateSize(), ms));
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => map.invalidateSize()) : null;
    ro?.observe(fullRef.current);

    // Live user-location blue dot (watches position while picker is open)
    let watchId: number | null = null;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const p = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
          setUserLoc(p);
          const blueDot = L.divIcon({
            html: `<div style="width:18px;height:18px;background:#1a73e8;border-radius:50%;border:3px solid white;box-shadow:0 0 0 2px rgba(26,115,232,0.35),0 2px 6px rgba(0,0,0,0.3);"></div>`,
            className: '',
            iconSize: [18, 18],
            iconAnchor: [9, 9],
          });
          if (!userDotRef.current) {
            userDotRef.current = L.marker([p.lat, p.lng], { icon: blueDot, interactive: false, keyboard: false }).addTo(map);
          } else {
            userDotRef.current.setLatLng([p.lat, p.lng]);
          }
          if (userAccuracyRef.current) {
            userAccuracyRef.current.setLatLng([p.lat, p.lng]);
            userAccuracyRef.current.setRadius(p.accuracy || 0);
          } else {
            userAccuracyRef.current = L.circle([p.lat, p.lng], {
              radius: p.accuracy || 0,
              color: '#1a73e8',
              fillColor: '#1a73e8',
              fillOpacity: 0.12,
              weight: 0,
              interactive: false,
            }).addTo(map);
          }
        },
        () => { /* ignore */ },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
      );
    }

    return () => {
      if (watchId != null && navigator.geolocation) navigator.geolocation.clearWatch(watchId);
      timers.forEach(clearTimeout);
      ro?.disconnect();
      map.off('moveend', updateFromCenter);
      map.remove();
      fullMap.current = null;
      userDotRef.current = null;
      userAccuracyRef.current = null;
    };
  }, [open]);

  // Reverse-geocode temp coords when they stabilize
  useEffect(() => {
    if (!open || !tempCoords) return;
    const t = setTimeout(async () => {
      const addr = await reverseGeocode(tempCoords.lat, tempCoords.lng);
      if (addr) setTempAddress(addr);
    }, 500);
    return () => clearTimeout(t);
  }, [tempCoords, open]);

  const openPicker = async () => {
    setTempAddress(address);
    if (lat != null && lng != null) {
      setTempCoords({ lat, lng });
    } else if (navigator.geolocation) {
      setLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setTempCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setLocating(false);
        },
        () => {
          setTempCoords(DEFAULT_LOCATION);
          setLocating(false);
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } else {
      setTempCoords(DEFAULT_LOCATION);
    }
    setOpen(true);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation || !fullMap.current) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        fullMap.current!.setView([pos.coords.latitude, pos.coords.longitude], 17);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const searchLocation = async () => {
    if (!searchInput.trim() || !fullMap.current) return;
    const result = await geocodeAddressCached(searchInput);
    if (result) {
      fullMap.current.setView([result.latitude, result.longitude], 17);
    }
  };

  const confirmPin = () => {
    if (!tempCoords) return;
    onChange({ lat: tempCoords.lat, lng: tempCoords.lng, address: tempAddress });
    setOpen(false);
  };

  const hasCoords = lat != null && lng != null;

  return (
    <>
      {hasCoords ? (
        <div className="relative rounded-xl overflow-hidden border border-border isolate">
          <div ref={previewRef} className="h-40 w-full" />
          <button
            type="button"
            onClick={openPicker}
            className="absolute left-1/2 bottom-3 -translate-x-1/2 z-[1000] bg-white text-foreground text-sm font-semibold px-4 py-2 rounded-full shadow-lg border border-border"
          >
            Adjust pin
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={openPicker}
          className="w-full h-24 border-dashed flex flex-col gap-1"
        >
          <MapPin className="h-5 w-5 text-primary" />
          <span className="text-sm">Set pin on map</span>
        </Button>
      )}

      {open && (
        <div className="fixed inset-0 z-[9999] bg-background flex flex-col">
          <div className="relative flex-1">
            <div ref={fullRef} className="absolute inset-0" />

            {/* Center floating pin (independent of map pan) */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full z-[1000]">
              <div className="flex flex-col items-center">
                <div className="bg-foreground text-background text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg mb-1 whitespace-nowrap">
                  Mark building entrance
                </div>
                <div className="w-6 h-6 rounded-full bg-primary border-4 border-white shadow-lg" />
                <div className="w-1 h-5 bg-primary -mt-1 rounded-b" />
              </div>
            </div>

            {/* Top bar */}
            <div className="absolute top-4 left-4 right-4 z-[1100] flex gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-10 w-10 rounded-full bg-white shadow-lg flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex-1 bg-white rounded-full shadow-lg flex items-center px-3 gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchLocation()}
                  placeholder="Search address"
                  className="border-0 h-10 focus-visible:ring-0 shadow-none px-0"
                />
              </div>
            </div>

            {/* Current-location button */}
            <button
              type="button"
              onClick={useCurrentLocation}
              disabled={locating}
              className="absolute bottom-56 right-4 z-[1100] h-11 w-11 rounded-full bg-white shadow-lg flex items-center justify-center disabled:opacity-50"
            >
              <Navigation className="h-5 w-5 text-primary" />
            </button>
          </div>

          {/* Bottom sheet */}
          <div className="bg-card border-t border-border p-5 space-y-3 rounded-t-3xl -mt-6 relative z-[1100]">
            <h2 className="text-lg font-bold text-foreground">Set delivery address</h2>
            <div className="bg-muted rounded-xl px-4 py-3">
              <p className="text-sm text-foreground truncate">{tempAddress || 'Move the map to select a location'}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Move the pin to your building entrance to help your courier find you faster
            </p>
            <Button
              type="button"
              onClick={confirmPin}
              disabled={!tempCoords}
              className="w-full h-12 rounded-full"
            >
              Set address
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default DeliveryPinPicker;
